import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Sparkles, Loader2, Check, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WaitlistSection = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [interest, setInterest] = useState('both');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);

  useEffect(() => {
    fetchWaitlistCount();
  }, []);

  const fetchWaitlistCount = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/waitlist/count`);
      setWaitlistCount(res.data.count);
    } catch (error) {
      // Silently fail
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/waitlist`, {
        email,
        name,
        interest,
      });
      setSubmitted(true);
      toast.success(t('waitlist.success') || 'You\'re on the list!');
      setWaitlistCount(prev => prev + 1);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('waitlist.alreadyJoined') || 'This email is already on the waitlist');
      } else {
        toast.error(t('waitlist.error') || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="h-8 w-8 text-white" />
          </div>
          <h2 className="font-serif text-3xl font-bold mb-4">
            {t('waitlist.thankYou') || "You're on the list!"}
          </h2>
          <p className="text-muted-foreground text-lg">
            {t('waitlist.thankYouDesc') || "We'll notify you as soon as WEDVAYA launches in your area."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-background" id="waitlist">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" />
            {t('waitlist.badge') || 'Coming Soon'}
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
            {t('waitlist.title') || 'Be the First to Know'}
          </h2>
          <p className="text-muted-foreground text-lg mb-2">
            {t('waitlist.subtitle') || 'Join our waitlist and get early access when we launch in your region.'}
          </p>
          {waitlistCount > 10 && (
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Users className="h-4 w-4" />
              {waitlistCount.toLocaleString()}+ {t('waitlist.alreadyJoined2') || 'people already joined'}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {t('waitlist.nameLabel') || 'Your Name'}
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('waitlist.namePlaceholder') || 'John Doe'}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {t('waitlist.emailLabel') || 'Email Address'} *
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('waitlist.emailPlaceholder') || 'you@example.com'}
                required
                className="w-full"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-1.5">
              {t('waitlist.interestLabel') || "I'm interested as a..."}
            </label>
            <Select value={interest} onValueChange={setInterest}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="guest">{t('waitlist.interestGuest') || 'Guest - I want to attend weddings'}</SelectItem>
                <SelectItem value="host">{t('waitlist.interestHost') || 'Host - I want to invite guests'}</SelectItem>
                <SelectItem value="both">{t('waitlist.interestBoth') || 'Both - I\'m interested in both'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button type="submit" className="w-full rounded-full py-6 text-lg" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t('common.loading') || 'Loading...'}
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-5 w-5" />
                {t('waitlist.submit') || 'Join the Waitlist'}
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center mt-4">
            {t('waitlist.privacy') || 'We respect your privacy. No spam, unsubscribe anytime.'}
          </p>
        </form>
      </div>
    </section>
  );
};

export default WaitlistSection;
